/**
 * $ref{} resolution logic — determines status and resolved value.
 * Used by both L0 pretty widgets and L2 mark decorations.
 */

import type { Decoration } from "@codemirror/view";
import {
    getContentMemberValue,
    extractTemplateParams,
    parseAssetRefPath,
    parseMagicMd,
    resolveDocumentAssetPath,
    splitScopedMemberPath,
    substituteTemplateInputRefs,
    type AssetRefParseResult,
    type ContentDefinition,
} from "@magic-md/core";
import { REF_FULL_RE } from "@magic-md/core/patterns";
import { t as translate } from "@magic-md/i18n";
import { useEditorStore } from "../../../store/editor-store";
import { usePluginExecutionStore } from "../../../store/plugin-execution-store";
import type { RefStatus } from "../types";
import { refMarksByStatus } from "./marks";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

export type RefAccessor = "output" | "body" | "variable" | "asset" | "unit";
export type RefSourceKind = "definition" | "content" | "for" | "asset" | "unit";

/**
 * Pre-fetched store snapshots to avoid repeated getState() calls
 * inside hot loops (buildDecorations scans every line on every keystroke).
 */
export interface RefResolveContext {
    documentPath?: string | null;
    parsedDoc: ReturnType<typeof useEditorStore.getState>["parsedDocument"];
    executions: ReturnType<typeof usePluginExecutionStore.getState>["executions"];
    forExecutions: ReturnType<typeof usePluginExecutionStore.getState>["forExecutions"];
    contentExecutions: ReturnType<typeof usePluginExecutionStore.getState>["contentExecutions"];
    importExecutions: ReturnType<typeof usePluginExecutionStore.getState>["importExecutions"];
    engineDefinitions: ReturnType<typeof usePluginExecutionStore.getState>["engineDefinitions"];
    engineContentDefinitions: ReturnType<typeof usePluginExecutionStore.getState>["engineContentDefinitions"];
    getOutput: ReturnType<typeof usePluginExecutionStore.getState>["getOutput"];
}

interface ParsedRefContent {
    definitionId: string;
    accessor: RefAccessor;
    outputPath: string;
}

const pathSegmentsCache = new Map<string, string[]>();
const PATH_CACHE_MAX = 512;

export function formatRefDisplayPath(
    accessor: RefAccessor,
    outputPath: string,
): string {
    if (accessor === "variable") {
        return outputPath || "value";
    }
    if (accessor === "asset") {
        return outputPath || "assets";
    }
    if (accessor === "unit") {
        return "unit";
    }
    return accessor === "body"
        ? "body"
        : outputPath ? `output.${outputPath}` : "output";
}

function isContentAccessorPath(rest: string): boolean {
    return rest === "body" || rest.startsWith("body.") || rest === "content" || rest.startsWith("content.");
}

export function getRefMissingTargetMessage(accessor: RefAccessor): string {
    if (accessor === "body") return tr("refResolution.contentMissing");
    if (accessor === "variable") return tr("refResolution.variableMissing");
    if (accessor === "asset") return tr("refResolution.assetMissing");
    if (accessor === "unit") return tr("refResolution.unitMissing");
    return tr("refResolution.definitionMissing");
}

export function getRefStatusMessage(
    status: RefStatus,
    accessor: RefAccessor,
    outputPath: string,
): string | null {
    switch (status) {
        case "loop-var":
            return outputPath
                ? tr("refResolution.loopVariablePath", { path: formatRefDisplayPath(accessor, outputPath) })
                : tr("refResolution.loopVariable");
        case "scope-var":
            return outputPath
                ? tr("refResolution.scopeVariablePath", { path: formatRefDisplayPath(accessor, outputPath) })
                : tr("refResolution.scopeVariable");
        case "running":
            return tr("refResolution.running");
        case "pending":
            return tr("refResolution.pending");
        case "error":
            return tr("refResolution.error");
        case "unresolved":
            return accessor === "body"
                ? tr("refResolution.contentMissing")
                : accessor === "variable"
                    ? outputPath
                        ? tr("refResolution.variablePathMissing", { path: formatRefDisplayPath(accessor, outputPath) })
                        : tr("refResolution.variableMissing")
                    : accessor === "asset"
                        ? outputPath
                            ? tr("refResolution.assetPathMissing", { path: outputPath })
                            : tr("refResolution.assetPathInvalid")
                    : accessor === "unit"
                        ? tr("refResolution.unitMissing")
                        : tr("refResolution.pathMissing", { path: formatRefDisplayPath(accessor, outputPath) });
        default:
            return null;
    }
}

/** Create a RefResolveContext from current store state (call once per buildDecorations) */
export function createRefResolveContext(): RefResolveContext {
    const editorState = useEditorStore.getState();
    const execState = usePluginExecutionStore.getState();
    const activeTab = editorState.tabs.find((tab) => tab.id === editorState.activeTabId);
    return {
        documentPath: activeTab?.filePath ?? null,
        parsedDoc: editorState.parsedDocument,
        executions: execState.executions,
        forExecutions: execState.forExecutions,
        contentExecutions: execState.contentExecutions,
        importExecutions: execState.importExecutions,
        engineDefinitions: execState.engineDefinitions,
        engineContentDefinitions: execState.engineContentDefinitions,
        getOutput: execState.getOutput,
    };
}

function resolveAssetRefDisplayValue(
    assetRef: AssetRefParseResult,
    ctx: RefResolveContext | undefined,
    parsedDoc: RefResolveContext["parsedDoc"],
): string {
    const sourceDocumentPath = assetRef.namespace
        ? (
            parsedDoc?.imports?.find((imp) => imp.namespace === assetRef.namespace)?.output
                ?.document?.resolvedPath
            ?? ctx?.importExecutions?.get(assetRef.namespace)?.document?.resolvedPath
            ?? null
        )
        : (ctx?.documentPath ?? null);
    return sourceDocumentPath
        ? resolveDocumentAssetPath(sourceDocumentPath, assetRef.relativePath) ?? assetRef.relativePath
        : assetRef.relativePath;
}

function getMergedContentDefinitions(
    parsedDoc: RefResolveContext["parsedDoc"],
    engineDefinitions?: RefResolveContext["engineDefinitions"],
    engineContentDefinitions?: RefResolveContext["engineContentDefinitions"],
    contentExecutions?: RefResolveContext["contentExecutions"],
) {
    const merged = new Map(parsedDoc?.contentDefinitions ?? new Map());

    for (const [id, def] of engineContentDefinitions ?? new Map()) {
        if (!merged.has(id)) {
            merged.set(id, def);
        }
    }

    if (parsedDoc?.uses) {
        const definitionIds = new Set<string>([
            ...Array.from(parsedDoc.definitions?.keys?.() ?? []),
            ...Array.from(engineDefinitions?.keys?.() ?? []),
        ]);

        for (const use of parsedDoc.uses) {
            if ((parsedDoc.contentDefinitions?.has(use.id) ?? false) || definitionIds.has(use.id)) continue;

            const contentSource = merged.get(use.source);
            if (!contentSource) continue;

            const useOutput = use.output;
            const existingContent = merged.get(use.id);
            const cachedContentOutput =
                useOutput?.contentOutput ??
                contentExecutions?.get(use.id)?.output ??
                existingContent?.output;

            const expandedBody = useOutput?.materializedBody ??
                (contentSource.template
                    ? substituteTemplateInputRefs(contentSource.body, use.with)
                    : contentSource.body);
            const params = extractTemplateParams(expandedBody);
            const remainsTemplate = use.template === true || params.length > 0;

            merged.set(use.id, {
                ...contentSource,
                id: use.id,
                template: remainsTemplate,
                params: params.length > 0 ? params : undefined,
                body: expandedBody,
                ...(cachedContentOutput
                    ? { output: cachedContentOutput }
                    : {}),
            });
        }
    }

    return merged;
}

export function resolveRef(
    refContent: string,
    insideFor = false,
    ctx?: RefResolveContext,
): {
    definitionId: string;
    accessor: RefAccessor;
    sourceKind: RefSourceKind;
    isImported: boolean;
    outputPath: string;
    status: RefStatus;
    resolvedValue: unknown;
} {
    const parsedDoc = ctx?.parsedDoc ?? useEditorStore.getState().parsedDocument;
    const assetRef = parseAssetRefPath(refContent);
    if (assetRef) {
        return {
            definitionId: assetRef.namespace ? `${assetRef.namespace}/assets` : "assets",
            accessor: "asset",
            sourceKind: "asset",
            isImported: Boolean(assetRef.namespace),
            outputPath: assetRef.relativePath,
            status: "resolved",
            resolvedValue: resolveAssetRefDisplayValue(assetRef, ctx, parsedDoc),
        };
    }

    const mergedContentDefinitions = getMergedContentDefinitions(
        parsedDoc,
        ctx?.engineDefinitions,
        ctx?.engineContentDefinitions,
        ctx?.contentExecutions,
    );

    const parsed = parseRefContent(refContent);
    if (!parsed) {
        const dotIdx = refContent.indexOf(".");
        return {
            definitionId: dotIdx === -1 ? refContent : refContent.slice(0, dotIdx),
            accessor: "output",
            sourceKind: "definition",
            isImported: isImportedRefTarget(
                dotIdx === -1 ? refContent : refContent.slice(0, dotIdx),
                parsedDoc,
            ),
            outputPath: dotIdx === -1 ? "" : refContent.slice(dotIdx + 1),
            status: "unresolved",
            resolvedValue: undefined,
        };
    }

    let { definitionId, accessor, outputPath } = parsed;
    const isImported = isImportedRefTarget(definitionId, parsedDoc);

    if (accessor === "variable") {
        if (definitionId.startsWith("$")) {
            return {
                definitionId,
                accessor,
                sourceKind: "for",
                isImported: false,
                outputPath,
                status: insideFor ? "loop-var" : "unresolved",
                resolvedValue: undefined,
            };
        }

        return {
            definitionId,
            accessor,
            sourceKind: "content",
            isImported: false,
            outputPath,
            status: "scope-var",
            resolvedValue: undefined,
        };
    }

    if (accessor === "unit") {
        const exists =
            mergedContentDefinitions.has(definitionId) ||
            parsedDoc?.definitions?.has(definitionId) ||
            parsedDoc?.forDefinitions?.has(definitionId) ||
            parsedDoc?.imports?.some((imp) => imp.namespace === definitionId) ||
            ctx?.engineDefinitions.has(definitionId);
        return {
            definitionId,
            accessor,
            sourceKind: "unit",
            isImported,
            outputPath,
            status: exists ? "resolved" : "unresolved",
            resolvedValue: exists ? definitionId : undefined,
        };
    }

    // Use pre-fetched context if available, otherwise fall back to store access
    const contentDef = mergedContentDefinitions.get(definitionId);
    const forDef = parsedDoc?.forDefinitions?.get(definitionId);
    const pluginDef = parsedDoc?.definitions?.get(definitionId)
        ?? ctx?.engineDefinitions.get(definitionId);
    const scopedContentMember = splitScopedMemberPath(
        definitionId,
        (id) => mergedContentDefinitions.has(id),
        (id) =>
            mergedContentDefinitions.has(id) ||
            !!parsedDoc?.definitions?.has(id) ||
            !!parsedDoc?.forDefinitions?.has(id) ||
            !!ctx?.engineDefinitions.has(id),
    );
    const sourceKind: RefSourceKind = contentDef || scopedContentMember
        ? "content"
        : forDef
            ? "for"
            : "definition";

    // Normalize legacy content.output.body refs to the body accessor so the UI
    // matches the body-only content ref model.
    if (contentDef && accessor === "output" && outputPath === "body") {
        accessor = "body";
    }

    // Body accessor — resolve from content definitions
    if (accessor === "body") {
        const memberContentDef = !contentDef && scopedContentMember
            ? findNestedContentDefinition(
                mergedContentDefinitions.get(scopedContentMember.rootId),
                scopedContentMember.memberPath,
            )
            : null;
        const bodyContentDef = contentDef ?? memberContentDef;
        if (!bodyContentDef) {
            return {
                definitionId,
                accessor,
                sourceKind,
                isImported,
                outputPath,
                status: "unresolved",
                resolvedValue: undefined,
            };
        }
        // body is always a string, sub-path not applicable
        return {
            definitionId,
            accessor,
            sourceKind: "content",
            isImported,
            outputPath: "",
            status: "resolved",
            resolvedValue: bodyContentDef.body,
        };
    }

    if (!contentDef && scopedContentMember && accessor === "output") {
        const rootId = scopedContentMember.rootId;
        const rootContentDef = mergedContentDefinitions.get(rootId);
        const contentExecEntry = ctx
            ? ctx.contentExecutions.get(rootId)
            : usePluginExecutionStore.getState().contentExecutions.get(rootId);
        if (contentExecEntry?.status === "running") {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "running",
                resolvedValue: undefined,
            };
        }
        if (contentExecEntry?.status === "error") {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "error",
                resolvedValue: undefined,
            };
        }

        const contentOutput = contentExecEntry?.output ?? rootContentDef?.output;
        if (!contentOutput) {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "pending",
                resolvedValue: undefined,
            };
        }

        const memberOutput = getContentMemberValue(
            contentOutput,
            scopedContentMember.memberPath,
        );
        if (memberOutput === undefined) {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "unresolved",
                resolvedValue: undefined,
            };
        }

        if (outputPath) {
            const resolved = resolveNestedValue(memberOutput, outputPath);
            return resolved === undefined
                ? {
                    definitionId,
                    accessor,
                    sourceKind: "content",
                    isImported,
                    outputPath,
                    status: "unresolved",
                    resolvedValue: undefined,
                }
                : {
                    definitionId,
                    accessor,
                    sourceKind: "content",
                    isImported,
                    outputPath,
                    status: "resolved",
                    resolvedValue: resolved,
                };
        }

        return {
            definitionId,
            accessor,
            sourceKind: "content",
            isImported,
            outputPath,
            status: "resolved",
            resolvedValue: memberOutput,
        };
    }

    if (contentDef && accessor === "output") {
        const contentExecEntry = ctx
            ? ctx.contentExecutions.get(definitionId)
            : usePluginExecutionStore.getState().contentExecutions.get(definitionId);
        if (contentExecEntry?.status === "running") {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "running",
                resolvedValue: undefined,
            };
        }
        if (contentExecEntry?.status === "error") {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "error",
                resolvedValue: undefined,
            };
        }

        const output = contentExecEntry?.output ?? contentDef.output;
        if (!output) {
            return {
                definitionId,
                accessor,
                sourceKind: "content",
                isImported,
                outputPath,
                status: "pending",
                resolvedValue: undefined,
            };
        }

        if (outputPath) {
            const resolved = resolveNestedValue(output, outputPath);
            return resolved === undefined
                ? {
                    definitionId,
                    accessor,
                    sourceKind: "content",
                    isImported,
                    outputPath,
                    status: "unresolved",
                    resolvedValue: undefined,
                }
                : {
                    definitionId,
                    accessor,
                    sourceKind: "content",
                    isImported,
                    outputPath,
                    status: "resolved",
                    resolvedValue: resolved,
                };
        }

        return {
            definitionId,
            accessor,
            sourceKind: "content",
            isImported,
            outputPath,
            status: "resolved",
            resolvedValue: output,
        };
    }

    // Output accessor — resolve from plugin definitions and for-block definitions.
    if (!pluginDef && !forDef) {
        return {
            definitionId,
            accessor,
            sourceKind,
            isImported,
            outputPath,
            status: "unresolved",
            resolvedValue: undefined,
        };
    }

    if (forDef) {
        const forExecEntry = ctx
            ? ctx.forExecutions.get(definitionId)
            : usePluginExecutionStore.getState().forExecutions.get(definitionId);
        if (forExecEntry?.status === "running") {
            return {
                definitionId,
                accessor,
                sourceKind: "for",
                isImported,
                outputPath,
                status: "running",
                resolvedValue: undefined,
            };
        }
        if (forExecEntry?.status === "error") {
            return {
                definitionId,
                accessor,
                sourceKind: "for",
                isImported,
                outputPath,
                status: "error",
                resolvedValue: undefined,
            };
        }

        const output = forDef.output;
        if (!output) {
            return {
                definitionId,
                accessor,
                sourceKind: "for",
                isImported,
                outputPath,
                status: "pending",
                resolvedValue: undefined,
            };
        }

        if (outputPath) {
            const resolved = resolveNestedValue(output, outputPath);
            return resolved === undefined
                ? {
                    definitionId,
                    accessor,
                    sourceKind: "for",
                    isImported,
                    outputPath,
                    status: "unresolved",
                    resolvedValue: undefined,
                }
                : {
                    definitionId,
                    accessor,
                    sourceKind: "for",
                    isImported,
                    outputPath,
                    status: "resolved",
                    resolvedValue: resolved,
                };
        }

        return {
            definitionId,
            accessor,
            sourceKind: "for",
            isImported,
            outputPath,
            status: "resolved",
            resolvedValue: output,
        };
    }

    const execEntry = ctx
        ? ctx.executions.get(definitionId)
        : usePluginExecutionStore.getState().executions.get(definitionId);
    if (execEntry?.status === "running")
        return {
            definitionId,
            accessor,
            sourceKind,
            isImported,
            outputPath,
            status: "running",
            resolvedValue: undefined,
        };
    if (execEntry?.status === "error")
        return {
            definitionId,
            accessor,
            sourceKind,
            isImported,
            outputPath,
            status: "error",
            resolvedValue: undefined,
        };

    const execOutput = ctx
        ? ctx.getOutput(definitionId)
        : usePluginExecutionStore.getState().getOutput(definitionId);
    const fallbackOutput = pluginDef?.output;
    const output = execOutput ?? fallbackOutput;
    if (!output)
        return {
            definitionId,
            accessor,
            sourceKind,
            isImported,
            outputPath,
            status: "pending",
            resolvedValue: undefined,
        };

    if (outputPath) {
        const resolved = resolveNestedValue(output, outputPath);
        return resolved === undefined
            ? {
                definitionId,
                accessor,
                sourceKind,
                isImported,
                outputPath,
                status: "unresolved",
                resolvedValue: undefined,
            }
            : {
                definitionId,
                accessor,
                sourceKind,
                isImported,
                outputPath,
                status: "resolved",
                resolvedValue: resolved,
            };
    }

    return {
        definitionId,
        accessor,
        sourceKind,
        isImported,
        outputPath,
        status: "resolved",
        resolvedValue: output,
    };
}

function isImportedRefTarget(
    definitionId: string,
    parsedDoc: ReturnType<typeof useEditorStore.getState>["parsedDocument"],
): boolean {
    if (!parsedDoc?.imports?.length) return false;
    return parsedDoc.imports.some((imp) => definitionId.startsWith(`${imp.namespace}/`));
}

function findNestedContentDefinition(
    root: ContentDefinition | undefined,
    memberPath: readonly string[],
): ContentDefinition | null {
    let current = root;
    for (const member of memberPath) {
        if (!current) return null;
        let parsed: ReturnType<typeof parseMagicMd>;
        try {
            parsed = parseMagicMd(current.body);
        } catch {
            return null;
        }
        current = parsed.contentDefinitions?.get(member);
    }
    return current ?? null;
}

/** Get the L2 mark decoration for a $ref{} based on resolution status */
export function getRefStatusMark(
    refContent: string,
    insideFor = false,
    ctx?: RefResolveContext,
): Decoration {
    return refMarksByStatus[resolveRef(refContent, insideFor, ctx).status];
}

/** Navigate into an object by dot path, return the value */
export function resolveNestedValue(obj: unknown, path: string): unknown {
    if (!path) return obj;
    let segments = pathSegmentsCache.get(path);
    if (!segments) {
        segments = path.match(/[\w-]+|\[\d+\]/g)?.map((segment) =>
            segment.startsWith("[") && segment.endsWith("]")
                ? segment.slice(1, -1)
                : segment,
        ) ?? [];
        if (pathSegmentsCache.size >= PATH_CACHE_MAX) pathSegmentsCache.clear();
        pathSegmentsCache.set(path, segments);
    }
    let current: unknown = obj;
    for (const seg of segments) {
        if (current == null || typeof current !== "object") return undefined;
        current = (current as Record<string, unknown>)[seg];
    }
    return current;
}

/** Parse a $ref{...} string into definitionId + accessor + outputPath */
export function parseRefString(
    refStr: string,
): { definitionId: string; accessor: RefAccessor; outputPath: string } | null {
    const m = refStr.match(REF_FULL_RE);
    if (!m) return null;
    return parseRefContent(m[1]);
}

function parseRefContent(refPath: string): ParsedRefContent | null {
    const assetRef = parseAssetRefPath(refPath);
    if (assetRef) {
        return {
            definitionId: assetRef.namespace ? `${assetRef.namespace}/assets` : "assets",
            accessor: "asset",
            outputPath: assetRef.relativePath,
        };
    }

    if (refPath.startsWith("$")) {
        const dotIdx = refPath.indexOf(".");
        return {
            definitionId: dotIdx === -1 ? refPath : refPath.slice(0, dotIdx),
            accessor: "variable",
            outputPath: dotIdx === -1 ? "" : refPath.slice(dotIdx + 1),
        };
    }

    if (refPath.startsWith("input/")) {
        const dotIdx = refPath.indexOf(".");
        return {
            definitionId: dotIdx === -1 ? refPath : refPath.slice(0, dotIdx),
            accessor: "variable",
            outputPath: dotIdx === -1 ? "" : refPath.slice(dotIdx + 1),
        };
    }

    if (refPath.startsWith("unit/")) {
        const unitId = refPath.slice("unit/".length);
        if (unitId) {
            return {
                definitionId: unitId,
                accessor: "unit",
                outputPath: "",
            };
        }
    }

    const dotIdx = refPath.indexOf(".");
    if (dotIdx === -1) return null;
    const definitionId = refPath.slice(0, dotIdx);
    const rest = refPath.slice(dotIdx + 1);
    if (isContentAccessorPath(rest)) {
        return { definitionId, accessor: "body", outputPath: "" };
    }
    if (!rest.startsWith("output")) return null;
    const outputPath =
        rest.length > "output".length ? rest.slice("output.".length) : "";
    return { definitionId, accessor: "output", outputPath };
}

/** Format a value for tooltip display — smart truncation for large content */
export function formatTooltipValue(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") {
        if (value.length > 200) {
            return value.slice(0, 200) + `\n\n… (${value.length} chars total)`;
        }
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return "[]";
        // Show first 3 items compactly
        const preview = value.slice(0, 3).map((item) => {
            if (typeof item === "string")
                return `"${item.length > 30 ? item.slice(0, 27) + "…" : item}"`;
            if (typeof item === "object" && item !== null) return "{…}";
            return String(item);
        });
        const suffix = value.length > 3 ? `, … +${value.length - 3} more` : "";
        return `[${preview.join(", ")}${suffix}]  (${value.length} items)`;
    }
    try {
        const json = JSON.stringify(value, null, 2);
        const lines = json.split("\n");
        if (lines.length > 12) {
            return (
                lines.slice(0, 10).join("\n") + `\n\n… (${lines.length} lines total)`
            );
        }
        if (json.length > 400) {
            return json.slice(0, 400) + `\n\n… (${json.length} chars total)`;
        }
        return json;
    } catch {
        return String(value);
    }
}
